import { useState, useEffect, useContext, createContext } from "react";

// ─── Theme tokens ─────────────────────────────────────────────────────────────
const DARK = {
  bg:        "#0A0906",
  card:      "#161410",
  cardInner: "#0f0d0a",
  border:    "#2A2520",
  text:      "#F2EDE4",
  dim:       "#6B6457",
  gold:      "#C9A84C",
  ink:       "#0E0E0E",
  navBg:     "rgba(10,9,6,0.97)",
  codeBg:    "#070604",
  shadow:    "rgba(0,0,0,0.6)",
  grain:     0.025,
  scrollTrack: "#0A0906",
  scrollThumb: "#2A2520",
};

const LIGHT = {
  bg:        "#F7F3EB",
  card:      "#FDFAF4",
  cardInner: "#F0EAE0",
  border:    "#DDD5C4",
  text:      "#1C1710",
  dim:       "#7A6E5E",
  gold:      "#9A6A1A",
  ink:       "#FDFAF4",
  navBg:     "rgba(247,243,235,0.97)",
  codeBg:    "#EEE8DC",
  shadow:    "rgba(0,0,0,0.08)",
  scrollTrack: "#F7F3EB",
  scrollThumb: "#DDD5C4",
  grain:     0.015,
};

const ThemeCtx = createContext(DARK);
const useT = () => useContext(ThemeCtx);

// ─── Utility Components ───────────────────────────────────────────────────────

function Tag({ children, color }) {
  const T = useT();
  const c = color || T.gold;
  return (
    <span style={{
      display: "inline-block",
      fontSize: 9, letterSpacing: 2.5, textTransform: "uppercase",
      color: c, border: `1px solid ${c}50`,
      padding: "3px 9px", borderRadius: 2,
      fontFamily: "'IBM Plex Mono', monospace",
    }}>{children}</span>
  );
}

function SectionLabel({ children }) {
  const T = useT();
  return (
    <div style={{
      fontSize: 8, letterSpacing: 4, textTransform: "uppercase",
      color: T.dim, marginBottom: 16,
      fontFamily: "'IBM Plex Mono', monospace",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <span style={{ width: 20, height: 1, background: T.gold, display: "inline-block" }} />
      {children}
    </div>
  );
}

function Divider() {
  const T = useT();
  return <div style={{ height: 1, background: T.border, margin: "56px 0" }} />;
}

// ─── Responsive Hook ───────────────────────────────────────────────────────
function useResponsive() {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return { isMobile, isTablet, isDesktop: !isMobile && !isTablet };
}

// ─── Theme Toggle Button ──────────────────────────────────────────────────────

function ThemeToggle({ dark, onToggle }) {
  const T = useT();
  return (
    <button
      onClick={onToggle}
      title={dark ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 20,
        width: 44, height: 24,
        cursor: "pointer",
        position: "relative",
        transition: "background 0.3s, border-color 0.3s",
        flexShrink: 0,
        display: "flex", alignItems: "center",
        padding: "0 3px",
      }}
    >
      {/* Track fill */}
      <div style={{
        position: "absolute", inset: 0, borderRadius: 20,
        background: dark ? T.gold + "20" : T.gold + "30",
        transition: "background 0.3s",
      }} />
      {/* Icons */}
      <span style={{
        position: "absolute", left: 5, fontSize: 10,
        opacity: dark ? 0.3 : 1, transition: "opacity 0.3s",
        lineHeight: 1,
      }}>☀</span>
      <span style={{
        position: "absolute", right: 5, fontSize: 9,
        opacity: dark ? 1 : 0.3, transition: "opacity 0.3s",
        lineHeight: 1, color: T.gold,
      }}>◑</span>
      {/* Thumb */}
      <div style={{
        width: 16, height: 16, borderRadius: "50%",
        background: T.gold,
        transform: dark ? "translateX(20px)" : "translateX(0px)",
        transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1)",
        position: "relative", zIndex: 1,
        boxShadow: `0 1px 4px ${T.shadow}`,
      }} />
    </button>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────

function Nav({ dark, onToggle }) {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const el = document.getElementById("qrm-scroll");
    if (!el) return;
    const handler = () => setScrolled(el.scrollTop > 20);
    el.addEventListener("scroll", handler);
    return () => el.removeEventListener("scroll", handler);
  }, []);

  const navLinks = ["How it works", "Domains", "Security", "Pricing", "Docs"];

  return (
    <nav style={{
      position: "sticky", top: 0, zIndex: 100,
      background: scrolled ? T.navBg : "transparent",
      borderBottom: `1px solid ${scrolled ? T.border : "transparent"}`,
      backdropFilter: scrolled ? "blur(12px)" : "none",
      padding: isMobile ? "0 16px" : "0 32px",
      transition: "background 0.3s, border-color 0.3s",
    }}>
      <div style={{ 
        maxWidth: 1040, 
        margin: "0 auto", 
        height: 56, 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between", 
        gap: 16 
      }}>
        <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: isMobile ? 18 : 20, color: T.gold }}>Quorum</div>
        
        {/* Desktop Navigation */}
        {!isMobile && (
          <div style={{ display: "flex", gap: isTablet ? 16 : 24, flex: 1, justifyContent: "center" }}>
            {navLinks.map(l => (
              <span key={l} style={{ 
                fontSize: 11, 
                color: T.dim, 
                cursor: "pointer", 
                fontFamily: "'IBM Plex Mono', monospace", 
                letterSpacing: 0.5, 
                whiteSpace: "nowrap",
                display: isTablet && l.length > 10 ? "none" : "block"
              }}>{l}</span>
            ))}
          </div>
        )}
        
        {/* Right side actions */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <ThemeToggle dark={dark} onToggle={onToggle} />
          
          {/* Desktop CTA */}
          {!isMobile && (
            <button style={{
              background: T.gold + "15", 
              border: `1px solid ${T.gold}40`,
              borderRadius: 4, 
              padding: "7px 16px",
              color: T.gold, 
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10, 
              letterSpacing: 1, 
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}>GET API KEY</button>
          )}
          
          {/* Mobile menu button */}
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: "transparent",
                border: "none",
                color: T.text,
                fontSize: 20,
                cursor: "pointer",
                padding: 4,
              }}
            >
              {mobileMenuOpen ? "✕" : "☰"}
            </button>
          )}
        </div>
      </div>
      
      {/* Mobile menu */}
      {isMobile && mobileMenuOpen && (
        <div style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          marginTop: 8,
          padding: "16px",
          boxShadow: `0 4px 12px ${T.shadow}`,
        }}>
          {navLinks.map(l => (
            <div key={l} style={{
              padding: "12px 0",
              fontSize: 14,
              color: T.text,
              fontFamily: "'IBM Plex Mono', monospace",
              letterSpacing: 0.5,
              cursor: "pointer",
              borderBottom: `1px solid ${T.border}`,
            }}>
              {l}
            </div>
          ))}
          <button style={{
            background: T.gold + "15", 
            border: `1px solid ${T.gold}40`,
            borderRadius: 4, 
            padding: "10px 16px",
            color: T.gold, 
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10, 
            letterSpacing: 1, 
            cursor: "pointer",
            width: "100%",
            marginTop: 8,
          }}>GET API KEY</button>
        </div>
      )}
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  const [node, setNode] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNode(n => (n + 1) % 4), 900);
    return () => clearInterval(t);
  }, []);

  const nodes = ["A", "B", "C", "D"];
  const colors = [T.gold, "#4A9EBF", "#BF4A7A", "#4ABF8A"];

  return (
    <section style={{ 
      padding: isMobile ? "40px 0 32px" : isTablet ? "60px 0 48px" : "80px 0 64px", 
      display: "grid", 
      gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 350px" : "1fr 420px", 
      gap: isMobile ? 32 : isTablet ? 40 : 48, 
      alignItems: "center" 
    }}>
      <div>
        <div style={{ marginBottom: isMobile ? 16 : 20 }}><Tag>Open Protocol · v1.0</Tag></div>
        <h1 style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: isMobile ? 42 : isTablet ? 52 : 62, 
          lineHeight: 1.05, 
          color: T.text,
          marginBottom: isMobile ? 16 : 20, 
          letterSpacing: -1,
        }}>
          Every critical<br />action needs<br />
          <span style={{ color: T.gold }}>more than one key.</span>
        </h1>
        <p style={{ 
          fontSize: isMobile ? 14 : 16, 
          color: T.dim, 
          lineHeight: 1.7, 
          maxWidth: isMobile ? "100%" : 480, 
          marginBottom: isMobile ? 24 : 32, 
          fontFamily: "'Lora', serif" 
        }}>
          Quorum is an open distributed-authentication protocol that requires multiple authorised parties to collectively approve a sensitive action — before any system executes it.
        </p>
        <div style={{ display: "flex", gap: isMobile ? 8 : 12, flexWrap: "wrap" }}>
          <button style={{
            background: T.gold, color: T.ink, border: "none",
            borderRadius: 4, 
            padding: isMobile ? "11px 20px" : "13px 28px",
            fontFamily: "'IBM Plex Mono', monospace", 
            fontSize: isMobile ? 10 : 11,
            letterSpacing: 2, cursor: "pointer", fontWeight: 700,
          }}>GET API ACCESS</button>
          <button style={{
            background: "transparent", color: T.text,
            border: `1px solid ${T.border}`,
            borderRadius: 4, 
            padding: isMobile ? "11px 20px" : "13px 28px",
            fontFamily: "'IBM Plex Mono', monospace", 
            fontSize: isMobile ? 10 : 11,
            letterSpacing: 2, cursor: "pointer",
          }}>VIEW DOCS</button>
        </div>
        <div style={{ 
          display: "flex", 
          gap: isMobile ? 16 : 28, 
          marginTop: isMobile ? 24 : 36,
          flexDirection: isMobile ? "column" : "row"
        }}>
          {[["m-of-n","Configurable thresholds"], ["Zero-trust","No secrets leave devices"], ["REST + SDK","Drop-in integration"]].map(([h, s]) => (
            <div key={h}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: isMobile ? 10 : 11, color: T.gold, marginBottom: 3 }}>{h}</div>
              <div style={{ fontSize: isMobile ? 9 : 10, color: T.dim }}>{s}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ 
        background: T.card, 
        border: `1px solid ${T.border}`, 
        borderRadius: 12, 
        padding: isMobile ? 20 : 28, 
        boxShadow: `0 4px 24px ${T.shadow}`,
        order: isMobile ? -1 : 0
      }}>
        <div style={{ fontSize: 9, letterSpacing: 3, color: T.dim, marginBottom: 20, fontFamily: "'IBM Plex Mono', monospace" }}>
          LIVE QUORUM · REQUEST #7F2A
        </div>
        <div style={{ background: T.cardInner, border: `1px solid ${T.gold}30`, borderRadius: 8, padding: "12px 14px", marginBottom: 20, textAlign: "center" }}>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: T.dim, marginBottom: 4 }}>PENDING ACTION</div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: isMobile ? 14 : 16, color: T.text }}>Release $240,000 escrow</div>
          <div style={{ fontSize: 9, color: T.dim, marginTop: 3 }}>→ Beneficiary: Kamau Developers Ltd</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {nodes.map((id, i) => {
            const done = i <= node;
            return (
              <div key={id} style={{
                display: "flex", alignItems: "center", gap: 10,
                background: done ? T.cardInner : "transparent",
                border: `1px solid ${done ? colors[i] + "40" : T.border}`,
                borderRadius: 6, padding: "8px 12px", transition: "all 0.4s ease",
              }}>
                <div style={{
                  width: isMobile ? 20 : 24, 
                  height: isMobile ? 20 : 24, 
                  borderRadius: "50%",
                  border: `1.5px solid ${done ? colors[i] : T.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, color: done ? colors[i] : T.border,
                  fontFamily: "'IBM Plex Mono', monospace",
                  boxShadow: done ? `0 0 8px ${colors[i]}40` : "none",
                  transition: "all 0.4s", flexShrink: 0,
                }}>{id}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: isMobile ? 9 : 10, color: done ? T.text : T.dim }}>
                    {["Alice Otieno","Brian Kamau","Carol Njeri","David Mwangi"][i]}
                  </div>
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: done ? colors[i] : T.border }}>
                  {done ? "AUTHORISED" : "PENDING"}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {nodes.map((id, i) => (
            <div key={id} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: i <= node ? colors[i] : T.border,
              boxShadow: i <= node ? `0 0 6px ${colors[i]}60` : "none",
              transition: "all 0.4s ease",
            }} />
          ))}
        </div>
        <div style={{ marginTop: 8, fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, color: T.dim, textAlign: "right" }}>
          {node + 1}/4 SIGNATURES COLLECTED
        </div>
      </div>
    </section>
  );
}

// ─── Problem ──────────────────────────────────────────────────────────────────

function Problem() {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  
  return (
    <section>
      <SectionLabel>The Problem</SectionLabel>
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", 
        gap: isMobile ? 32 : isTablet ? 40 : 48 
      }}>
        <div>
          <h2 style={{ 
            fontFamily: "'DM Serif Display', serif", 
            fontSize: isMobile ? 28 : isTablet ? 32 : 36, 
            color: T.text, 
            lineHeight: 1.2, 
            marginBottom: isMobile ? 16 : 20 
          }}>
            Single-point authorisation is a single point of failure.
          </h2>
          <p style={{ 
            color: T.dim, 
            fontSize: isMobile ? 13 : 14, 
            lineHeight: 1.8, 
            fontFamily: "'Lora', serif" 
          }}>
            Most systems still rely on one person, one password, one approval to execute high-value or high-risk actions. That person can be coerced, compromised, or unavailable. The system has no way to tell the difference.
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            { text: "A single finance manager approves wire transfers alone", sub: "Internal fraud. No checks." },
            { text: "A patient's full record released with one doctor's login", sub: "Breached credential = breached privacy." },
            { text: "A board resolution signed off by one authorised signatory", sub: "Bypasses governance. Legally questionable." },
            { text: "A contract executed from a single digital signature", sub: "No multi-party consent. No audit trail." },
          ].map(({ text, sub }) => (
            <div key={text} style={{
              background: T.card, 
              border: `1px solid ${T.border}`,
              borderRadius: 8, 
              padding: isMobile ? "10px 12px" : "12px 14px",
              display: "flex", 
              gap: isMobile ? 10 : 12, 
              alignItems: "flex-start",
              boxShadow: `0 1px 4px ${T.shadow}`,
            }}>
              <span style={{ color: "#C0392B", fontSize: 13, marginTop: 1 }}>⚠</span>
              <div>
                <div style={{ fontSize: isMobile ? 11 : 12, color: T.text, marginBottom: 2 }}>{text}</div>
                <div style={{ fontSize: isMobile ? 9 : 10, color: T.dim }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── How It Works ─────────────────────────────────────────────────────────────

function HowItWorks() {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  const [active, setActive] = useState(0);
  const steps = [
    {
      n: "01", title: "Action Initiated",
      desc: "Any system triggers a Quorum auth request via a single API call, specifying the action, the quorum group, and the required threshold (e.g. 3-of-5).",
      code: `POST /v1/requests\n{\n  "group_id": "board-finance",\n  "action": "release_escrow",\n  "payload": { "amount": 240000 },\n  "threshold": { "required": 3, "of": 5 }\n}`,
    },
    {
      n: "02", title: "Request Broadcast",
      desc: "Quorum notifies each group member via their registered channel — push notification, SMS, email, or embedded widget — with the request details.",
      code: `// Webhook dispatched to each member\n{\n  "event": "auth.request",\n  "request_id": "QRM-7F2A",\n  "action": "release_escrow",\n  "expires_at": "2026-03-09T14:30:00Z",\n  "notify_via": ["push", "sms"]\n}`,
    },
    {
      n: "03", title: "Members Authorise",
      desc: "Each member authenticates on their own device using their PIN, biometric, or passkey. Quorum derives their key share client-side — the raw secret never leaves the device.",
      code: `// Client SDK — runs entirely on device\nquorum.authorize({\n  request_id: "QRM-7F2A",\n  credential: userPin,   // never transmitted\n  // → PBKDF2 share derived locally\n  // → only the share hash is sent\n})`,
    },
    {
      n: "04", title: "Composite Resolution",
      desc: "Once the threshold is met, Quorum's resolution engine assembles the submitted shares, derives the composite auth key, and compares it against the group's stored key hash.",
      code: `// Server-side resolution\nshares.map(pbkdf2Derive)\n  → concat → SHA256 → composite\n  → SHA256(composite) → authKey\n  → compare(authKey, storedHash)\n  → 200 OK { "status": "approved" }`,
    },
    {
      n: "05", title: "Callback to Your System",
      desc: "Quorum fires a signed webhook to your system with the approval result. Your system proceeds with the action. Full audit log written immutably.",
      code: `// Signed webhook → your endpoint\n{\n  "event": "request.approved",\n  "request_id": "QRM-7F2A",\n  "approved_by": ["A","B","D"],\n  "composite_hash": "3f9a…e12b",\n  "timestamp": "2026-03-09T14:28:41Z"\n}`,
    },
  ];

  return (
    <section>
      <SectionLabel>How It Works</SectionLabel>
      <h2 style={{ 
        fontFamily: "'DM Serif Display', serif", 
        fontSize: isMobile ? 28 : isTablet ? 32 : 36, 
        color: T.text, 
        marginBottom: isMobile ? 24 : 32 
      }}>Five steps. One API.</h2>
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "180px 1fr" : "220px 1fr", 
        gap: isMobile ? 20 : isTablet ? 20 : 24 
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {steps.map((s, i) => (
            <button key={i} onClick={() => setActive(i)} style={{
              background: active === i ? T.card : "transparent",
              border: `1px solid ${active === i ? T.gold + "40" : "transparent"}`,
              borderRadius: 7, 
              padding: isMobile ? "10px 12px" : "12px 14px", 
              textAlign: "left",
              cursor: "pointer", 
              transition: "all 0.2s",
              display: "flex", 
              gap: 10, 
              alignItems: "flex-start",
              boxShadow: active === i ? `0 1px 4px ${T.shadow}` : "none",
            }}>
              <span style={{ 
                fontFamily: "'IBM Plex Mono', monospace", 
                fontSize: 9, 
                color: active === i ? T.gold : T.border, 
                marginTop: 2 
              }}>{s.n}</span>
              <span style={{ 
                fontSize: isMobile ? 11 : 12, 
                color: active === i ? T.text : T.dim, 
                lineHeight: 1.4 
              }}>{s.title}</span>
            </button>
          ))}
        </div>
        <div style={{
          background: T.card, 
          border: `1px solid ${T.border}`,
          borderRadius: 10, 
          padding: isMobile ? "16px 18px" : "24px 26px",
          display: "flex", 
          flexDirection: "column", 
          gap: 18,
          boxShadow: `0 2px 12px ${T.shadow}`,
        }}>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ 
              fontFamily: "'DM Serif Display', serif", 
              fontSize: isMobile ? 32 : 40, 
              color: T.gold + "30", 
              lineHeight: 1, 
              flexShrink: 0 
            }}>{steps[active].n}</div>
            <div>
              <h3 style={{ 
                fontFamily: "'DM Serif Display', serif", 
                fontSize: isMobile ? 18 : 22, 
                color: T.text, 
                marginBottom: 8 
              }}>{steps[active].title}</h3>
              <p style={{ 
                fontSize: isMobile ? 12 : 13, 
                color: T.dim, 
                lineHeight: 1.7, 
                fontFamily: "'Lora', serif" 
              }}>{steps[active].desc}</p>
            </div>
          </div>
          <div style={{
            background: T.codeBg, 
            border: `1px solid ${T.border}`,
            borderRadius: 7, 
            padding: isMobile ? "12px 14px" : "16px 18px",
            fontFamily: "'IBM Plex Mono', monospace", 
            fontSize: isMobile ? 10 : 11,
            color: T.gold + "CC", 
            lineHeight: 1.8, 
            whiteSpace: "pre",
            overflowX: "auto",
          }}>
            {steps[active].code}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Domains ──────────────────────────────────────────────────────────────────

function Domains() {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  const [hovered, setHovered] = useState(null);
  const domains = [
    { icon: "◈", name: "Finance & Banking",    color: T.gold,    scenario: "Joint account withdrawals, treasury outflows, inter-bank transfers, loan disbursements", compliance: "PCI-DSS · AML · KYC", trigger: "Any debit above threshold triggers quorum of account holders + compliance officer" },
    { icon: "◉", name: "Healthcare",           color: "#4A9EBF", scenario: "Sensitive record access, prescription of controlled substances, patient data release to third parties", compliance: "HIPAA · GDPR · HL7", trigger: "Full record access requires attending physician + specialist + patient consent" },
    { icon: "◆", name: "Legal & Contracts",    color: "#BF8A4A", scenario: "Multi-signatory contract execution, escrow release, power-of-attorney activation", compliance: "e-IDAS · UETA · eSign", trigger: "Contract executes only when all named signatories have independently authorised" },
    { icon: "◇", name: "Corporate Governance", color: "#9A4ABF", scenario: "Board resolutions, M&A approvals, large procurement sign-offs, data access grants", compliance: "SOX · ISO 27001", trigger: "Board action requires quorum of directors (e.g. 4-of-7) to approve via their devices" },
    { icon: "○", name: "Real Estate",          color: "#4ABF7A", scenario: "Multi-owner property sale, rental income release, mortgage drawdown authorisation", compliance: "AML · Land registry", trigger: "Title transfer only proceeds when all co-owners have independently confirmed" },
    { icon: "□", name: "Supply Chain",         color: "#BF4A4A", scenario: "Goods release, payment-on-delivery, customs clearance, cross-border fund release", compliance: "UCC · Incoterms · OFAC", trigger: "Payment released only when shipper, receiver, and escrow agent all confirm delivery" },
  ];

  return (
    <section>
      <SectionLabel>Domains</SectionLabel>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "flex-end", 
        marginBottom: isMobile ? 24 : 32, 
        flexWrap: "wrap", 
        gap: 16 
      }}>
        <h2 style={{ 
          fontFamily: "'DM Serif Display', serif", 
          fontSize: isMobile ? 28 : isTablet ? 32 : 36, 
          color: T.text, 
          maxWidth: isMobile ? "100%" : 480 
        }}>One protocol. Infinite applications.</h2>
        <p style={{ 
          fontSize: isMobile ? 12 : 13, 
          color: T.dim, 
          maxWidth: isMobile ? "100%" : 340, 
          lineHeight: 1.7, 
          fontFamily: "'Lora', serif" 
        }}>
          Quorum is domain-agnostic. Any system that executes a sensitive action on behalf of multiple stakeholders is a fit.
        </p>
      </div>
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)", 
        gap: isMobile ? 12 : 10 
      }}>
        {domains.map((d, i) => (
          <div key={d.name}
            onMouseEnter={() => setHovered(i)} 
            onMouseLeave={() => setHovered(null)}
            style={{
              background: hovered === i ? T.card : T.bg,
              border: `1px solid ${hovered === i ? d.color + "40" : T.border}`,
              borderRadius: 10, 
              padding: isMobile ? "16px 14px" : "20px 18px",
              cursor: "default", 
              transition: "all 0.25s",
              boxShadow: hovered === i ? `0 4px 20px ${T.shadow}` : "none",
            }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
              <span style={{ color: d.color, fontSize: isMobile ? 14 : 16 }}>{d.icon}</span>
              <span style={{ 
                fontFamily: "'DM Serif Display', serif", 
                fontSize: isMobile ? 14 : 16, 
                color: T.text 
              }}>{d.name}</span>
            </div>
            <p style={{ 
              fontSize: isMobile ? 10 : 11, 
              color: T.dim, 
              lineHeight: 1.65, 
              marginBottom: 12, 
              fontFamily: "'Lora', serif" 
            }}>{d.scenario}</p>
            <div style={{
              background: T.cardInner, 
              border: `1px solid ${d.color}20`,
              borderRadius: 6, 
              padding: "8px 10px",
              fontSize: isMobile ? 9 : 10, 
              color: d.color + "99",
              fontFamily: "'IBM Plex Mono', monospace", 
              lineHeight: 1.6,
            }}>↳ {d.trigger}</div>
            <div style={{ 
              marginTop: 10, 
              fontFamily: "'IBM Plex Mono', monospace", 
              fontSize: 8, 
              color: T.border, 
              letterSpacing: 1 
            }}>{d.compliance}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Architecture ─────────────────────────────────────────────────────────────

function Architecture() {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  
  return (
    <section>
      <SectionLabel>Integration Architecture</SectionLabel>
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", 
        gap: isMobile ? 32 : isTablet ? 32 : 40, 
        alignItems: "start" 
      }}>
        <div>
          <h2 style={{ 
            fontFamily: "'DM Serif Display', serif", 
            fontSize: isMobile ? 28 : isTablet ? 32 : 34, 
            color: T.text, 
            marginBottom: isMobile ? 16 : 20 
          }}>Drops into any stack in under a day.</h2>
          <p style={{ 
            fontSize: isMobile ? 13 : 14, 
            color: T.dim, 
            lineHeight: 1.8, 
            marginBottom: isMobile ? 24 : 28, 
            fontFamily: "'Lora', serif" 
          }}>
            Quorum exposes a clean REST API and lightweight client SDKs. Your system calls Quorum when it needs multi-party approval. Quorum handles the broadcast, collection, resolution, and audit trail — then calls you back.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { layer: "REST API",         desc: "Language-agnostic. Authenticated with org-scoped API keys + HMAC signatures." },
              { layer: "Client SDKs",      desc: "JavaScript, Python, Java, Swift, Kotlin. Handles device-side key derivation locally." },
              { layer: "Webhooks",         desc: "Signed callbacks to your endpoints on request creation, member auth, approval, denial, timeout." },
              { layer: "Embed Widget",     desc: "Drop a pre-built auth UI into your web or mobile app with one script tag or npm package." },
              { layer: "Admin Dashboard",  desc: "Manage groups, thresholds, members, and view full request audit logs." },
            ].map(({ layer, desc }) => (
              <div key={layer} style={{ 
                display: "flex", 
                gap: isMobile ? 12 : 14, 
                borderBottom: `1px solid ${T.border}`, 
                paddingBottom: 10,
                flexDirection: isMobile ? "column" : "row"
              }}>
                <div style={{ 
                  fontFamily: "'IBM Plex Mono', monospace", 
                  fontSize: 10, 
                  color: T.gold, 
                  width: isMobile ? "auto" : 130, 
                  flexShrink: 0, 
                  paddingTop: 1,
                  marginBottom: isMobile ? 4 : 0
                }}>{layer}</div>
                <div style={{ 
                  fontSize: isMobile ? 12 : 12, 
                  color: T.dim, 
                  lineHeight: 1.6 
                }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { label: "YOUR APPLICATION",  color: "#4A9EBF", items: ["Triggers action", "POST /v1/requests", "Receives webhook callback"] },
            { label: "QUORUM API LAYER",  color: T.gold,    items: ["Request routing", "Group management", "Threshold enforcement", "Audit logging"] },
            { label: "RESOLUTION ENGINE", color: "#9A4ABF", items: ["Share assembly", "PBKDF2 derivation", "SHA-256 composite hash", "Stored key comparison"] },
            { label: "MEMBER DEVICES",    color: "#4ABF7A", items: ["Push / SMS / email", "Local PIN → key share", "Share hash submitted only"] },
          ].map(({ label, color, items }) => (
            <div key={label} style={{ 
              background: T.card, 
              border: `1px solid ${color}25`, 
              borderRadius: 8, 
              padding: isMobile ? "12px 14px" : "14px 16px", 
              boxShadow: `0 1px 6px ${T.shadow}` 
            }}>
              <div style={{ 
                fontFamily: "'IBM Plex Mono', monospace", 
                fontSize: 8, 
                color, 
                letterSpacing: 2, 
                marginBottom: 8 
              }}>{label}</div>
              <div style={{ 
                display: "flex", 
                gap: 6, 
                flexWrap: "wrap",
                flexDirection: isMobile ? "column" : "row"
              }}>
                {items.map(item => (
                  <span key={item} style={{
                    background: T.cardInner, 
                    border: `1px solid ${T.border}`,
                    borderRadius: 4, 
                    padding: isMobile ? "6px 10px" : "3px 8px",
                    fontFamily: "'IBM Plex Mono', monospace", 
                    fontSize: isMobile ? 10 : 9, 
                    color: T.dim,
                    textAlign: isMobile ? "center" : "left",
                    display: isMobile ? "block" : "inline-block"
                  }}>{item}</span>
                ))}
              </div>
            </div>
          ))}
          <div style={{ 
            textAlign: "center", 
            padding: "10px", 
            fontFamily: "'IBM Plex Mono', monospace", 
            fontSize: 8, 
            color: T.dim, 
            letterSpacing: 1 
          }}>
            ↕ Encrypted in transit · TLS 1.3 · No raw secrets stored or transmitted
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Security ─────────────────────────────────────────────────────────────────

function Security() {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  const props = [
    { title: "Zero raw-secret transmission",  body: "PINs and passphrases never leave the member's device. Only the PBKDF2-derived share hash is transmitted." },
    { title: "Stored commitment, not credential", body: "The vault holds only SHA-256(composite key). Quorum cannot reconstruct any individual credential from it." },
    { title: "Per-member salts",              body: "Each member has a unique, non-reusable salt. Compromise of one share cannot be used to brute-force another." },
    { title: "Threshold enforcement",         body: "Even if Quorum's server is fully compromised, an attacker cannot approve a request without all required member devices." },
    { title: "Tamper-evident audit trail",    body: "Every event is hash-chained and timestamped. Immutable for compliance purposes." },
    { title: "Request expiry & revocation",   body: "Auth requests expire after a configurable TTL. Any member can revoke a pending request. Replay attacks are impossible." },
  ];
  return (
    <section>
      <SectionLabel>Security Model</SectionLabel>
      <h2 style={{ 
        fontFamily: "'DM Serif Display', serif", 
        fontSize: isMobile ? 28 : isTablet ? 32 : 34, 
        color: T.text, 
        marginBottom: isMobile ? 24 : 32 
      }}>
        Designed to be compromised and still hold.
      </h2>
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)", 
        gap: isMobile ? 16 : 10 
      }}>
        {props.map(({ title, body }) => (
          <div key={title} style={{ 
            background: T.card, 
            border: `1px solid ${T.border}`, 
            borderRadius: 9, 
            padding: isMobile ? "20px 18px" : "18px 16px", 
            boxShadow: `0 1px 6px ${T.shadow}` 
          }}>
            <div style={{ 
              width: 6, 
              height: 6, 
              borderRadius: "50%", 
              background: T.gold, 
              marginBottom: 12, 
              boxShadow: `0 0 8px ${T.gold}60` 
            }} />
            <div style={{ 
              fontFamily: "'DM Serif Display', serif", 
              fontSize: isMobile ? 16 : 15, 
              color: T.text, 
              marginBottom: 8 
            }}>{title}</div>
            <div style={{ 
              fontSize: isMobile ? 12 : 11, 
              color: T.dim, 
              lineHeight: 1.7 
            }}>{body}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Use Case Minis ───────────────────────────────────────────────────────────

function UseCaseMini() {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  const cases = [
    { org: "Sacco Ltd",                  action: "Member loan disbursement above KES 500k requires 3-of-5 committee approval via Quorum.", domain: "Cooperative Finance" },
    { org: "Kenyatta Referral Hospital", action: "Access to patient HIV records requires consent from attending physician, data officer, and patient representative.", domain: "Healthcare" },
    { org: "Acacia Law Partners",        action: "Settlement funds in client escrow only released when all three named partners have independently authorised the instruction.", domain: "Legal" },
    { org: "City County Procurement",    action: "Any single-source contract above threshold requires 4-of-6 tender committee signatures before award.", domain: "Government" },
  ];
  return (
    <section>
      <SectionLabel>Scenario Examples</SectionLabel>
      <h2 style={{ 
        fontFamily: "'DM Serif Display', serif", 
        fontSize: isMobile ? 28 : isTablet ? 32 : 34, 
        color: T.text, 
        marginBottom: isMobile ? 24 : 28 
      }}>Real decisions. Real stakes.</h2>
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", 
        gap: isMobile ? 16 : 10 
      }}>
        {cases.map(({ org, action, domain }) => (
          <div key={org} style={{ 
            background: T.card, 
            border: `1px solid ${T.border}`, 
            borderRadius: 10, 
            padding: isMobile ? "18px 16px" : "20px 18px", 
            boxShadow: `0 1px 6px ${T.shadow}` 
          }}>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: isMobile ? "flex-start" : "center", 
              marginBottom: 12,
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 8 : 0
            }}>
              <div style={{ 
                fontFamily: "'DM Serif Display', serif", 
                fontSize: isMobile ? 16 : 16, 
                color: T.text 
              }}>{org}</div>
              <Tag color={T.dim}>{domain}</Tag>
            </div>
            <p style={{ 
              fontSize: isMobile ? 12 : 12, 
              color: T.dim, 
              lineHeight: 1.7, 
              fontFamily: "'Lora', serif" 
            }}>{action}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

function Pricing() {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  const [annual, setAnnual] = useState(false);
  const tiers = [
    {
      name: "Starter", tagline: "For builders and small teams",
      priceLabel: "Free", price: 0, color: T.dim,
      features: ["Up to 3 quorum groups","Up to 5 members per group","500 auth requests/month","REST API access","JavaScript SDK","Email notifications","30-day audit log"],
      cta: "Start Free",
    },
    {
      name: "Business", tagline: "For production systems",
      priceLabel: `$${annual ? 290 : 349}/mo`, price: annual ? 290 : 349,
      color: T.gold, highlight: true,
      features: ["Unlimited quorum groups","Up to 20 members per group","50,000 auth requests/month","All SDKs (JS, Python, Java, Swift, Kotlin)","Push + SMS + Email notifications","Embeddable widget","Webhook callbacks","1-year audit log","SLA 99.9% uptime"],
      cta: "Start Trial",
    },
    {
      name: "Enterprise", tagline: "For regulated industries",
      priceLabel: "Custom", price: null, color: "#9A4ABF",
      features: ["Unlimited everything","On-premise deployment option","Custom notification channels","HSM key storage integration","Biometric + passkey support","Compliance exports (HIPAA, SOX, PCI)","7-year immutable audit trail","SSO / SAML / LDAP","Priority SLA + dedicated support"],
      cta: "Contact Sales",
    },
  ];

  return (
    <section>
      <SectionLabel>Pricing</SectionLabel>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "flex-end", 
        marginBottom: isMobile ? 24 : 32, 
        flexWrap: "wrap", 
        gap: 16 
      }}>
        <h2 style={{ 
          fontFamily: "'DM Serif Display', serif", 
          fontSize: isMobile ? 28 : isTablet ? 32 : 34, 
          color: T.text 
        }}>Transparent pricing.<br />No per-transaction fees.</h2>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 4, 
          background: T.card, 
          border: `1px solid ${T.border}`, 
          borderRadius: 6, 
          padding: "4px" 
        }}>
          {["Monthly", "Annual (−17%)"].map((label, i) => (
            <button key={label} onClick={() => setAnnual(i === 1)} style={{
              background: annual === (i === 1) ? T.gold + "20" : "transparent",
              border: `1px solid ${annual === (i === 1) ? T.gold + "50" : "transparent"}`,
              borderRadius: 4, 
              padding: isMobile ? "4px 12px" : "5px 14px",
              color: annual === (i === 1) ? T.gold : T.dim,
              fontFamily: "'IBM Plex Mono', monospace", 
              fontSize: 9,
              letterSpacing: 1, 
              cursor: "pointer",
            }}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)", 
        gap: isMobile ? 16 : 12 
      }}>
        {tiers.map((t) => (
          <div key={t.name} style={{
            background: t.highlight ? T.card : T.bg,
            border: `1px solid ${t.highlight ? t.color + "50" : T.border}`,
            borderRadius: 12, 
            padding: isMobile ? "20px 18px" : "24px 22px",
            display: "flex", 
            flexDirection: "column",
            boxShadow: t.highlight ? `0 4px 24px ${T.shadow}` : `0 1px 4px ${T.shadow}`,
            position: "relative", 
            overflow: "hidden",
          }}>
            {t.highlight && (
              <div style={{
                position: "absolute", top: 14, right: 14,
                background: T.gold + "20", 
                border: `1px solid ${T.gold}50`,
                borderRadius: 3, 
                padding: "2px 8px",
                fontFamily: "'IBM Plex Mono', monospace", 
                fontSize: 7, 
                color: T.gold, 
                letterSpacing: 2,
              }}>MOST POPULAR</div>
            )}
            <div style={{ marginBottom: 20 }}>
              <div style={{ 
                fontFamily: "'DM Serif Display', serif", 
                fontSize: isMobile ? 18 : 22, 
                color: T.text, 
                marginBottom: 4 
              }}>{t.name}</div>
              <div style={{ 
                fontSize: isMobile ? 9 : 10, 
                color: T.dim, 
                marginBottom: 16 
              }}>{t.tagline}</div>
              <div style={{ 
                fontFamily: "'DM Serif Display', serif", 
                fontSize: isMobile ? 28 : 32, 
                color: t.color 
              }}>{t.priceLabel}</div>
              {t.price > 0 && <div style={{ 
                fontSize: 9, 
                color: T.dim, 
                marginTop: 2 
              }}>billed {annual ? "annually" : "monthly"}</div>}
            </div>
            <div style={{ 
              flex: 1, 
              display: "flex", 
              flexDirection: "column", 
              gap: 7, 
              marginBottom: isMobile ? 18 : 22 
            }}>
              {t.features.map(f => (
                <div key={f} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ 
                    color: t.color, 
                    fontSize: 10, 
                    flexShrink: 0, 
                    marginTop: 1 
                  }}>✓</span>
                  <span style={{ 
                    fontSize: isMobile ? 10 : 11, 
                    color: T.dim, 
                    lineHeight: 1.5 
                  }}>{f}</span>
                </div>
              ))}
            </div>
            <button style={{
              background: t.highlight ? T.gold : "transparent",
              border: `1px solid ${t.highlight ? T.gold : t.color + "50"}`,
              borderRadius: 6, 
              padding: isMobile ? "10px" : "11px",
              color: t.highlight ? T.ink : t.color,
              fontFamily: "'IBM Plex Mono', monospace", 
              fontSize: 10,
              letterSpacing: 2, 
              cursor: "pointer", 
              fontWeight: t.highlight ? 700 : 400,
            }}>{t.cta}</button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  const T = useT();
  const { isMobile, isTablet } = useResponsive();
  
  return (
    <footer style={{ 
      borderTop: `1px solid ${T.border}`, 
      paddingTop: isMobile ? 32 : 40, 
      marginTop: isMobile ? 40 : 56 
    }}>
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "1fr 1fr 1fr 1fr", 
        gap: isMobile ? 24 : 32 
      }}>
        <div>
          <div style={{ 
            fontFamily: "'DM Serif Display', serif", 
            fontSize: isMobile ? 20 : 22, 
            color: T.gold, 
            marginBottom: 8 
          }}>Quorum</div>
          <div style={{ 
            fontSize: isMobile ? 12 : 11, 
            color: T.dim, 
            lineHeight: 1.7 
          }}>Open distributed-authentication protocol for multi-party action approval.</div>
        </div>
        {[
          { head: "Product",  links: ["Documentation","API Reference","SDKs","Changelog","Status"] },
          { head: "Use Cases", links: ["Finance","Healthcare","Legal","Governance","Supply Chain"] },
          { head: "Company",  links: ["About","Security","Compliance","Blog","Contact"] },
        ].map(({ head, links }) => (
          <div key={head}>
            <div style={{ 
              fontFamily: "'IBM Plex Mono', monospace", 
              fontSize: 9, 
              letterSpacing: 2, 
              color: T.gold, 
              marginBottom: isMobile ? 10 : 12 
            }}>{head.toUpperCase()}</div>
            {links.map(l => (
              <div 
                key={l} 
                style={{ 
                  fontSize: isMobile ? 12 : 11, 
                  color: T.dim, 
                  marginBottom: isMobile ? 8 : 7, 
                  cursor: "pointer",
                  wordBreak: "break-word",
                  overflowWrap: "break-word"
                }}
              >
                {l}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div style={{ 
        marginTop: isMobile ? 32 : 40, 
        paddingTop: isMobile ? 16 : 20, 
        borderTop: `1px solid ${T.border}`, 
        display: "flex", 
        justifyContent: "space-between", 
        flexWrap: "wrap", 
        gap: 10,
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "flex-start" : "center"
      }}>
        <div style={{ 
          fontFamily: "'IBM Plex Mono', monospace", 
          fontSize: isMobile ? 10 : 9, 
          color: T.dim,
          wordBreak: "break-word",
          overflowWrap: "break-word"
        }}>© 2026 Quorum Protocol. Open-source core. Apache 2.0.</div>
        <div style={{ 
          fontFamily: "'IBM Plex Mono', monospace", 
          fontSize: isMobile ? 10 : 9, 
          color: T.dim,
          wordBreak: "break-word",
          overflowWrap: "break-word"
        }}>SOC 2 Type II · ISO 27001 · GDPR Ready</div>
      </div>
    </footer>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [dark, setDark] = useState(() => {
    // Check system theme preference on initial load
    if (typeof window !== 'undefined') {
      const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const savedTheme = localStorage.getItem('quorum-theme');
      
      // Use saved theme if exists, otherwise use system preference
      return savedTheme ? savedTheme === 'dark' : systemPreference;
    }
    return true; // Default to dark for SSR
  });
  
  const { isMobile } = useResponsive();
  const T = dark ? DARK : LIGHT;

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e) => {
        // Only change theme if user hasn't manually set a preference
        const savedTheme = localStorage.getItem('quorum-theme');
        if (!savedTheme) {
          setDark(e.matches);
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  // Save theme preference when manually changed
  const handleThemeToggle = () => {
    const newTheme = !dark;
    setDark(newTheme);
    if (typeof window !== 'undefined') {
      localStorage.setItem('quorum-theme', newTheme ? 'dark' : 'light');
    }
  };

  return (
    <ThemeCtx.Provider value={T}>
      <div id="qrm-scroll" style={{
        background: T.bg, minHeight: "100vh",
        overflowY: "auto", overflowX: "hidden",
        transition: "background 0.35s ease",
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Lora:ital,wght@0,400;0,500;1,400&family=IBM+Plex+Mono:wght@400;600;700&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          button { font-family: inherit; }
          #qrm-scroll { scrollbar-width: thin; scrollbar-color: ${T.scrollThumb} ${T.scrollTrack}; }
          #qrm-scroll::-webkit-scrollbar { width: 4px; }
          #qrm-scroll::-webkit-scrollbar-track { background: ${T.scrollTrack}; }
          #qrm-scroll::-webkit-scrollbar-thumb { background: ${T.scrollThumb}; border-radius: 2px; }
        `}</style>

        {/* Grain overlay */}
        <div style={{
          position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
          opacity: T.grain,
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }} />

        <Nav dark={dark} onToggle={handleThemeToggle} />

        <div style={{ 
          maxWidth: 1040, 
          margin: "0 auto", 
          padding: isMobile ? "0 16px" : "0 32px", 
          position: "relative", 
          zIndex: 1 
        }}>
          <Hero />
          <Divider />
          <Problem />
          <Divider />
          <HowItWorks />
          <Divider />
          <Domains />
          <Divider />
          <Architecture />
          <Divider />
          <Security />
          <Divider />
          <UseCaseMini />
          <Divider />
          <Pricing />
          <Footer />
        </div>
      </div>
    </ThemeCtx.Provider>
  );
}